import { CSSProperties, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { MapPin, Plus } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useIsSm } from '@/lib/hooks/useMediaQuery.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useNewIssueDialogStore } from '@/routes/projectStages/issues/lib/stores/useNewIssueDialogStore.ts'
import { useLicenceAllowsCreate } from '@/lib/licence/useStageLicence.ts'
import { isIssuesListRoute } from '@/routes/projectStages/lib/newIssueEntryRoutes.ts'
import { useGlobalNewIssueEntry } from '@/routes/projectStages/issues/lib/hooks/useGlobalNewIssueEntry.ts'
import { useQuickCaptureEntry } from '@/lib/quickCapture/useQuickCaptureEntry.ts'
import { useMobileFabStore } from '@/store/useMobileFabStore.ts'

// Gate: visible on mobile while the create dialog isn't already open.
//  - On the per-issueType list route (not the detail) it adds an issue of the
//    URL's type, in that type's colour, when the user may create it.
//  - On every other stage screen except the issue detail (see
//    matchGlobalNewIssueRoute) it is the global "Report" entry point: the
//    dialog opens in global mode, where the user picks the type, so the button
//    is neutral rather than tinted with whatever type happens to be current.
// The New Issue dialog host lives in ProjectsRoot, so both work without
// navigating first. Same position and size in both roles, so the right-hand
// lane SlowConnectionPrompt reserves for the FAB still holds.
//
// Where the stage's quick-capture pilot is open (see useQuickCaptureEntry), a
// second round button, "Report on plan", stacks ABOVE the "+" in the same
// lane and width; the "+" itself keeps its position and behaviour. Without
// that entry the markup is exactly the single button.
const FAB_POSITION: CSSProperties = {
  bottom: `calc(var(--bottomTabBarHeight) + var(--safe-area-inset-bottom) + 16px)`,
}
// One FAB (56px, size-14) plus a 12px gap above FAB_POSITION.
const STACKED_FAB_POSITION: CSSProperties = {
  bottom: `calc(var(--bottomTabBarHeight) + var(--safe-area-inset-bottom) + 16px + 56px + 12px)`,
}
const FAB_CLASS = `fixed right-4 z-[90] flex size-14 items-center justify-center rounded-full shadow-lg transition-transform active:scale-95 md:hidden`

export default function MobileFab() {
  const isMobile = useIsSm()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const currIssueType = useProjectStageStore(s => s.currIssueType)
  const dialogOpen = useNewIssueDialogStore(s => s.dialogOpen)
  const setDialogOpen = useNewIssueDialogStore(s => s.updateDialogState)
  const setDraftIssueId = useNewIssueDialogStore(s => s.setDraftIssueId)
  const licenceAllowsCreate = useLicenceAllowsCreate()
  const globalEntry = useGlobalNewIssueEntry()
  // Not gated on the dialog: it opens and closes without a new screen entry,
  // and gating would spend another read on every close.
  const quickEntry = useQuickCaptureEntry({ enabled: isMobile })
  const setStacked = useMobileFabStore(s => s.setStacked)

  const newIssueFab = renderNewIssueFab()
  // Published for PushPromptCard, which spans the full width just above this
  // lane and must clear BOTH buttons when they stack (see useMobileFabStore).
  // Layout effect, so the card moves in the same paint the second FAB appears.
  const stacked = isMobile && !dialogOpen && quickEntry.available && newIssueFab !== null
  useLayoutEffect(() => {
    setStacked(stacked)
    return () => setStacked(false)
  }, [stacked, setStacked])

  if (!isMobile) return null
  if (dialogOpen) return null

  if (!quickEntry.available) return newIssueFab

  return (
    <>
      <button
        type="button"
        onClick={quickEntry.open}
        aria-label={t('quickCapture.reportOnPlan')}
        data-cy="mobile-fab-quick-capture"
        // Alone (the list's type is not creatable) it takes the "+" slot
        // rather than floating over an empty one.
        style={newIssueFab ? STACKED_FAB_POSITION : FAB_POSITION}
        className={cn(FAB_CLASS, `bg-zinc-900 text-zinc-50`)}
      >
        <MapPin className={`size-6`} strokeWidth={2.5} />
      </button>
      {newIssueFab}
    </>
  )

  function renderNewIssueFab() {
    if (!isIssuesListRoute(pathname)) {
      if (!globalEntry.available) return null
      return (
        <button
          type="button"
          onClick={globalEntry.open}
          aria-label={t('project.issues.reportIssue')}
          data-cy="mobile-fab-report-issue"
          style={FAB_POSITION}
          className={cn(FAB_CLASS, `bg-zinc-900 text-zinc-50`)}
        >
          <Plus className={`size-6`} strokeWidth={2.5} />
        </button>
      )
    }

    if (!currIssueType?.currentUserCanCreate || !licenceAllowsCreate) return null
    const issueTypeColor = currIssueType.color

    const openNewIssue = () => {
      // Clear any stale draftIssueId from a previous draft-edit session.
      // Without this, useGetDraftIssueData would refetch that issue and
      // prepopulate the "new issue" form with its title / attachments.
      setDraftIssueId(null)
      setDialogOpen(true)
    }

    return (
      <button
        type="button"
        onClick={openNewIssue}
        aria-label={t('project.issues.newIssue')}
        data-cy="mobile-fab-new-issue"
        style={{ ...FAB_POSITION, backgroundColor: issueTypeColor }}
        className={cn(FAB_CLASS, `text-white`)}
      >
        <Plus className={`size-6`} strokeWidth={2.5} />
      </button>
    )
  }
}
